<template>
  <div class="mt-2">
    <template v-if="loading">
      <v-skeleton-loader class="mb-2" type="heading" />
      <v-skeleton-loader class="mb-2" type="text" />
      <v-row density="comfortable">
        <v-col>
          <v-skeleton-loader class="mb-2" type="list-item-three-line" />
        </v-col>
        <v-col>
          <v-skeleton-loader class="mb-2" type="list-item-three-line" />
        </v-col>
      </v-row>
      <v-divider class="my-3" />
      <v-skeleton-loader class="mb-2" type="text" />
      <v-skeleton-loader v-for="index in 6" :key="index" class="mb-2" type="list-item-avatar" />
    </template>
    <template v-else>
      <v-row>
        <v-col cols="12">
          <div class="d-flex flex-row align-center mb-1">
            <sf-sub-heading class="mr-2">
              {{ $t('orderLimits.activateOrderLimits') }}
            </sf-sub-heading>
            <v-switch
              id="pw-activate-order-limits-switch"
              v-model="defaultOrderLimit.activated"
              :disabled="!ownUser.permission.shopAdmin"
              class="mt-0 pt-0"
              color="green"
              hide-details
              inset
              :aria-label="$t('orderLimits.activateOrderLimits')"
              @update:model-value="orderLimitActivatedChanged"
            />
          </div>
          <div class="text-justify">
            <sf-text>{{ $t('orderLimits.description') }}</sf-text>
          </div>
        </v-col>
      </v-row>
      <template v-if="defaultOrderLimit.activated">
        <order-limit-default-settings :editable="ownUser.permission.shopAdmin" />
        <template v-if="showColleaguesPermissions">
          <list-header :title="t('company.employee').toString()" class="font-weight-medium">
            <template #search>
              <search-bar :value="searchTerm" @search="setKeyword" @clear="resetSearch" />
            </template>
            <template v-if="$vuetify.display.mdAndUp" #cols>
              <v-col class="d-flex align-center justify-start">
                <sf-text>{{ $t('name') }}</sf-text>
              </v-col>
              <v-col class="d-flex align-center justify-end">
                <sf-text>{{ $t('orderLimits.orderRight') }}</sf-text>
              </v-col>
              <v-col class="d-flex align-center justify-end">
                <sf-text>{{ $t('orderLimits.orderLimit') }}</sf-text>
              </v-col>
              <v-col class="d-flex align-center justify-end">
                <sf-text>{{ $t('orderLimits.orderApprover') }}</sf-text>
              </v-col>
              <v-col
                v-if="ownUser.permission.shopAdmin"
                class="d-flex align-center justify-end mx-1"
                cols="auto"
              >
                <v-icon size="36" color="transparent" :icon="mdiPencil" />
              </v-col>
            </template>
          </list-header>
          <template v-if="orderLimitUsersFiltered.length === 0">
            <list-empty
              :message="$t('orderLimits.noEmployeesFound').toString()"
              :icon="mdiAccountCircleOutline"
            />
          </template>
          <list-content v-else>
            <v-col id="pw-order-limit-users" class="px-0 pb-1">
              <order-limit-user-card
                v-for="user in orderLimitUsersFiltered"
                :key="user.id"
                :user="user"
                :approvers="approvers"
                :editable="ownUser.permission.shopAdmin"
                :currency="portalSettings.currency"
              />
            </v-col>
          </list-content>
        </template>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import ListHeader from '@/components/list/ListHeader.vue'
import ListContent from '@/components/list/ListContent.vue'
import ListEmpty from '@/components/list/ListEmpty.vue'
import SearchBar from '@/components/SearchBar.vue'
import SfSubHeading from '@/components/text/SfSubHeading.vue'
import SfText from '@/components/text/SfText.vue'
import { useTitle } from '@/helpers'
import OrderLimitDefaultSettings from '@/modules/orderLimits/components/OrderLimitDefaultSettings.vue'
import OrderLimitUserCard from '@/modules/orderLimits/components/OrderLimitUserCard.vue'
import useOrderLimits from '@/modules/orderLimits/useOrderLimits'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings'
import MyCompanyEvent from '@/modules/tracking/events/MyCompany'
import { trackEvent } from '@/modules/tracking/useTracking'
import useOwnUser from '@/modules/user/useOwnUser'
import { mdiPencil, mdiAccountCircleOutline } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { sortByName, nameAndEmailFilter } from '@/modules/user/helpers'

const { t } = useI18n()
useTitle(ref(`${t('myCompany')} - ${t('windowTitle.CompanyOrderLimit')}`))
const ownUserStore = useOwnUser()
const store = useOrderLimits()

const { defaultOrderLimit, orderLimitUsers, approvers, loading } = storeToRefs(store)
const { ownUser } = storeToRefs(ownUserStore)

const { portalSettings, loading: portalSettingsLoading } = storeToRefs(usePortalSettings())

watch(
  portalSettingsLoading,
  (newValue) => {
    if (!newValue) {
      store.loadOrderLimits()
      store.getApprovers()
    }
  },
  { immediate: true }
)

const searchTerm = ref<string | undefined>(undefined)

const setKeyword = (keyword: string) => {
  keyword = keyword.trim()
  if (keyword === '') {
    searchTerm.value = undefined
    return
  }
  searchTerm.value = keyword
}

const resetSearch = () => {
  searchTerm.value = undefined
}

const orderLimitUsersFiltered = computed(() => {
  const keyword = searchTerm.value
  if (!keyword || keyword == null) {
    return [...orderLimitUsers.value].sort(sortByName)
  }
  return orderLimitUsers.value.filter(nameAndEmailFilter(keyword)).sort(sortByName)
})

const showColleaguesPermissions = computed<boolean>(
  () => !portalSettings.value.hideColleaguesPermissions
)

const orderLimitActivatedChanged = () => {
  const trackingEvent = defaultOrderLimit.value.activated
    ? MyCompanyEvent.OrderLimit.Default.Activated
    : MyCompanyEvent.OrderLimit.Default.Deactivated
  trackEvent(trackingEvent)
  store.updateDefaultOrderLimit({
    activated: defaultOrderLimit.value.activated
  })
}
</script>
